import { projectsService } from '@/services/projectsService';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

/**
 * ProjectSwitcher component for switching between projects in the top navbar
 * @returns {JSX.Element} ProjectSwitcher component
 */
const ProjectSwitcher = () => {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Fetch all projects
  const {
    data: projectsData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['projects'],
    queryFn: projectsService.getProjects,
    staleTime: 5 * 60 * 1000,
  });

  const projects = projectsData?.docs || [];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = event => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleProjectSelect = targetProjectId => {
    setIsOpen(false);
    navigate(`/projects/${targetProjectId}/dashboard`);
  };

  if (isLoading) {
    return (
      <div className="h-4 w-4 border-2 border-gray-300 border-t-primary-500 rounded-full animate-spin"></div>
    );
  }

  if (error || projects.length === 0) {
    return null;
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center p-1 rounded-md hover:bg-gray-200 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        title="Switch project"
      >
        <svg
          className={`h-4 w-4 text-gray-600 transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute left-0 mt-2 w-64 bg-white rounded-lg shadow-xl py-1 z-50 border border-gray-200 overflow-hidden transform transition-all duration-200 origin-top-left">
          <div className="px-4 py-2 border-b border-gray-100 bg-gray-50">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Switch Project
            </p>
          </div>

          <div className="max-h-60 overflow-y-auto py-1 scrollbar-thin scrollbar-thumb-gray-200">
            {projects.map(project => {
              const pId = project._id || project.id;
              const isCurrent = pId === projectId;

              return (
                <button
                  key={pId}
                  onClick={() => handleProjectSelect(pId)}
                  className={`w-full text-left px-4 py-2.5 text-sm flex items-center justify-between hover:bg-primary-50 transition-colors ${
                    isCurrent
                      ? 'bg-primary-50 text-primary-700 font-medium'
                      : 'text-gray-700'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <div
                      className={`w-2 h-2 rounded-full ${project.isActive ? 'bg-green-500' : 'bg-gray-300'}`}
                    ></div>
                    <span className="truncate max-w-[160px]">
                      {project.name}
                    </span>
                  </div>
                  {isCurrent && (
                    <svg
                      className="h-4 w-4 text-primary-500"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                </button>
              );
            })}
          </div>

          <div className="border-t border-gray-100 p-2 bg-gray-50 text-center">
            <button
              onClick={() => {
                setIsOpen(false);
                navigate('/projects');
              }}
              className="text-xs text-primary-600 font-medium hover:text-primary-700 transition-colors"
            >
              View all projects
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectSwitcher;
